import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { Buffer } from 'buffer';
import { saveAs } from 'file-saver';

import { Link as GatsbyLink } from 'gatsby';
import {
  Link,
  Alert,
  Heading,
  Spinner,
} from '@printdeal/ui-library';
import { useQueryParam, StringParam } from 'use-query-params';

import Breadcrumb from '../../../../Breadcrumb';
import { messages } from '../../../translations/quote';
import QuoteDetail from './QuoteDetail';
import Head from '../../../../PrivateHead';
import { Quote, QuoteStatus } from '../../../../../types/quotes/quotes';
import { ServiceFactory } from '../../../../../services/ServiceFactory';
import { useQuoteStatuses } from '../../hooks/useQuotes';

const STATUS = {
  PENDING: 'pending',
  LOADED: 'loaded',
  ERROR: 'error',
};

const PDF_STATUS = {
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
};

const QuoteDetailsPage = ({ quoteId }: { quoteId: string }) => {
  const intl = useIntl();
  const quotesApi = ServiceFactory.createQuotesService();

  const [pdfStatus, setPdfStatus] = useState(PDF_STATUS.LOADED);

  const [status, setStatus] = useState(STATUS.PENDING);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [statuses, setStatuses] = useState<QuoteStatus[] | null>(null);

  const downloadPdfHandle = async () => {
    window.Buffer = Buffer;
    const { quoteNumber } = quote as Quote;
    try {
      setPdfStatus(PDF_STATUS.LOADING);
      const pdf = await quotesApi?.downloadPdf(quoteId);
      setPdfStatus(PDF_STATUS.LOADED);
      const blob = new Blob([Buffer.from(pdf, 'base64')], { type: 'application/pdf' });
      saveAs(blob, quoteNumber);
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('PDF Download error', error);
      setPdfStatus(PDF_STATUS.ERROR);
    }
  };

  const fetchQuote = () => {
    quotesApi?.getQuote(quoteId).then((fetchedQuote) => {
      setQuote(fetchedQuote);
    }).catch(() => {
      setStatus(STATUS.ERROR);
    });
  };

  const {
    status: quoteStatus,
    results,
  } = useQuoteStatuses();

  useEffect(() => {
    fetchQuote();
  }, []);

  useEffect(() => {
    if (quoteStatus === 'success') {
      setStatuses(results);
    } else {
      setStatus(quoteStatus);
    }
  }, [quoteStatus]);

  useEffect(() => {
    if (quote && statuses) {
      setStatus(STATUS.LOADED);
    }
  }, [quote, statuses]);

  const [showSuccessAlert] = useQueryParam('quoteCreated', StringParam);
  return (
    <div className="data-sentry-block">
      <Head title={intl.formatMessage({
        id: messages.quoteDetailsPage.id,
        defaultMessage: messages.quoteDetailsPage.defaultMessage,
      })}
      />
      <Breadcrumb className="pd-mb-4">
        <Breadcrumb.Item to="../..">
          <FormattedMessage id="account.index.title" defaultMessage="My account" />
        </Breadcrumb.Item>
        <Breadcrumb.Item to="..">
          <FormattedMessage id="quotes.title" defaultMessage="My Quotes" />
        </Breadcrumb.Item>
        <>
          {status === STATUS.LOADED && (
            <Breadcrumb.Item isCurrentPage>
              <FormattedMessage
                id="quote.title"
                defaultMessage="Quote {quoteNumber}"
                values={{ quoteNumber: quote?.quoteNumber }}
              />
            </Breadcrumb.Item>
          )}
        </>
      </Breadcrumb>

      {status === STATUS.ERROR && (
        <Alert className="pd-mb-3" variant="error">
          <FormattedMessage
            id="quote.details.page.error.loading"
            defaultMessage="Something went wrong while fetching the quote."
          />
        </Alert>
      )}
      {showSuccessAlert && (
        <Alert className="pd-mb-3" variant="success">
          <FormattedMessage
            id="quote.details.page.created.success"
            defaultMessage="Quote created successfully."
          />
        </Alert>
      )}

      {status === STATUS.PENDING && (
        <div className="pd-flex pd-h-[300px] pd-flex-row pd-items-center pd-justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {status === STATUS.LOADED && (
        <>
          {pdfStatus === PDF_STATUS.ERROR && (
            <Alert className="pd-mb-3" variant="error">
              <FormattedMessage
                id="quote.details.page.error.pdfDownload"
                defaultMessage="Unable to download PDF."
              />
            </Alert>
          )}
          <div className="pd-mb-5 pd-flex pd-flex-col sm:pd-flex-row sm:pd-items-center sm:pd-text-left">
            <Heading size="2" className="pd-flex pd-grow pd-items-center pd-gap-2">
              <FormattedMessage
                id="quote.title"
                defaultMessage="Quote {quoteNumber}"
                values={{ quoteNumber: quote?.quoteNumber }}
              />
            </Heading>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              as="button"
              disabled={pdfStatus === PDF_STATUS.LOADING}
              onClick={downloadPdfHandle}
              iconBefore="download"
              className="pd-text-sm"
              variant="secondary"
            >
              <FormattedMessage
                id="quote.details.downloadPdf"
                defaultMessage="Download quote"
              />
            </Link>
          </div>
          <QuoteDetail quote={quote} statuses={statuses} />
        </>
      )}

      <Link
        as={GatsbyLink}
        to="../"
        iconBefore="chevron-left"
        variant="secondary"
      >
        <FormattedMessage
          id="quoteform.backToQuoteOverview"
          defaultMessage="Back to My Quotes"
        />
      </Link>
    </div>
  );
};

export default QuoteDetailsPage;
